import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, Eye, FileText, Pencil, Plus, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { insuranceAPI, INS_STATUS, aud } from '../../lib/insuranceAPI';
import { PolicyDialog } from './InsurancePolicyDialog';

export const InsuranceStatusBadge = ({ status }) => (
  <Badge variant="outline" className={INS_STATUS[status]?.cls} data-testid={`ins-status-${status}`}>{INS_STATUS[status]?.label}</Badge>
);

const Preview = ({ cert, onClose }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    insuranceAPI.blob(cert.id, true).then((b) => { u = URL.createObjectURL(b); setUrl(u); }).catch((e) => { toast.error(e?.response?.status === 423 ? 'File is still being virus-scanned' : 'Could not open file'); onClose(); });
    return () => u && URL.revokeObjectURL(u);
  }, [cert.id, onClose]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="ins-preview">
        <DialogHeader><DialogTitle className="truncate pr-6">{cert.filename}</DialogTitle></DialogHeader>
        {url && (cert.content_type.startsWith('image/') ? <img src={url} alt={cert.filename} className="max-h-[75vh] mx-auto object-contain" />
          : <iframe title={cert.filename} src={url} className="h-[75vh] w-full rounded border" />)}
      </DialogContent>
    </Dialog>
  );
};

const Certs = ({ certs, onPreview }) => (
  <div className="flex flex-wrap gap-1">
    {certs.length === 0 && <span className="text-xs text-muted-foreground">None</span>}
    {certs.map((c) => {
      const viewable = c.content_type === 'application/pdf' || c.content_type.startsWith('image/');
      const dl = async () => { try { const b = await insuranceAPI.blob(c.id, false); const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = c.filename; a.click(); } catch (e) { toast.error(e?.response?.status === 423 ? 'File is still being virus-scanned' : 'Download failed'); } };
      return (
        <span key={c.id} className="inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-xs" title={c.filename} data-testid={`ins-cert-${c.id}`}>
          <FileText className="h-3.5 w-3.5" /><span className="max-w-[110px] truncate">{c.filename}</span>
          {c.scan_status !== 'clean' && <span className="text-amber-700">(scanning)</span>}
          {viewable && <button onClick={() => onPreview(c)} className="p-0.5 hover:text-primary" aria-label="Preview" data-testid={`ins-cert-preview-${c.id}`}><Eye className="h-3.5 w-3.5" /></button>}
          <button onClick={dl} className="p-0.5 hover:text-primary" aria-label="Download" data-testid={`ins-cert-download-${c.id}`}><Download className="h-3.5 w-3.5" /></button>
        </span>);
    })}
  </div>
);

export const PMInsuranceTab = () => {
  const [rows, setRows] = useState(null);
  const [history, setHistory] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [preview, setPreview] = useState(null);
  const load = useCallback(() => insuranceAPI.list(history).then((d) => setRows(d.items)).catch(() => toast.error('Failed to load policies')), [history]);
  useEffect(() => { load(); }, [load]);
  const del = async (p) => {
    if (!window.confirm(`Delete the ${p.type_label} policy ${p.policy_number}?`)) return;
    try { await insuranceAPI.remove(p.id); toast.success('Policy deleted'); load(); } catch (e) { if (e?.response?.data?.detail !== 'STEP_UP_REQUIRED') toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };
  const done = () => { setDialog(null); load(); };

  return (
    <div className="mt-4 space-y-4" data-testid="pm-insurance-tab">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => setDialog({ mode: 'add' })} data-testid="ins-add-btn"><Plus className="mr-1 h-4 w-4" />Add policy</Button>
        <label className="ml-auto flex items-center gap-2 text-sm"><Switch checked={history} onCheckedChange={setHistory} data-testid="ins-history-toggle" />Show history</label>
      </div>
      {rows && rows.length === 0 && (
        <Card className="border-dashed" data-testid="ins-empty"><CardContent className="py-10 text-center space-y-2">
          <ShieldCheck className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="font-medium">No insurance recorded yet</p>
          <p className="text-sm text-muted-foreground">Add your professional indemnity (PI) and public liability (PL) policies and upload the certificates of currency.</p>
        </CardContent></Card>)}
      {rows && rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm" data-testid="ins-table">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>{['Type', 'Insurer', 'Policy no.', 'Cover', 'Period', 'Status', 'Certificates', ''].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>{rows.map((p) => (
              <tr key={p.id} className={`border-t align-top ${p.is_history ? 'opacity-60' : ''}`} data-testid={`ins-row-${p.id}`}>
                <td className="px-3 py-2 font-medium">{p.type_label}{p.is_history && <Badge variant="outline" className="ml-2 text-[10px]">Renewed</Badge>}</td>
                <td className="px-3 py-2">{p.insurer}</td>
                <td className="px-3 py-2 font-mono text-xs">{p.policy_number}</td>
                <td className="px-3 py-2">{aud(p.cover_amount)}{p.excess != null && <div className="text-xs text-muted-foreground">excess {aud(p.excess)}</div>}</td>
                <td className="px-3 py-2 whitespace-nowrap">{p.start_date} – {p.expiry_date}</td>
                <td className="px-3 py-2"><InsuranceStatusBadge status={p.status} /></td>
                <td className="px-3 py-2"><Certs certs={p.certificates} onPreview={setPreview} /></td>
                <td className="px-3 py-2"><span className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => setDialog({ mode: 'edit', policy: p })} aria-label="Edit" data-testid={`ins-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  {!p.is_history && <Button size="icon" variant="ghost" onClick={() => setDialog({ mode: 'renew', policy: p })} aria-label="Renew" data-testid={`ins-renew-${p.id}`}><RefreshCw className="h-4 w-4" /></Button>}
                  <Button size="icon" variant="ghost" onClick={() => del(p)} aria-label="Delete" data-testid={`ins-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                </span></td>
              </tr>))}</tbody>
          </table>
        </div>)}
      {dialog && <PolicyDialog {...dialog} onClose={() => setDialog(null)} onSaved={done} />}
      {preview && <Preview cert={preview} onClose={() => setPreview(null)} />}
    </div>
  );
};

export const InsuranceSummaryCard = ({ onOpen }) => {
  const [s, setS] = useState(null);
  useEffect(() => { insuranceAPI.summary().then(setS).catch(() => {}); }, []);
  if (!s) return null;
  const item = (k, label) => (
    <span className="flex items-center gap-2" data-testid={`ins-summary-${k}`}>{label}:
      {s[k] ? <><InsuranceStatusBadge status={s[k].status} /><span className="text-muted-foreground">expires {s[k].expiry_date}</span></> : <span className="text-muted-foreground">Not recorded</span>}
    </span>);
  return (
    <Card data-testid="ins-summary-card"><CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 py-4 text-sm">
      <span className="flex items-center gap-2 font-medium"><ShieldCheck className="h-4 w-4" />Insurance</span>
      {item('professional_indemnity', 'PI')}{item('public_liability', 'PL')}
      <Button size="sm" variant="ghost" className="ml-auto" onClick={onOpen} data-testid="ins-summary-open">Manage</Button>
    </CardContent></Card>
  );
};
